import { post } from '../../helpers/api';

/**
 * The station candidate sweep endpoints, on `AdminStationsController`
 * (`apps/admin/src/stations.controller.ts`). All four are POST with a body,
 * all under the `admin` global prefix, all behind
 * `ValidationPipe({ transform: true, whitelist: true })`.
 *
 * Three properties of that pipe shape this module:
 *
 * 1. `whitelist: true` STRIPS unknown properties instead of rejecting them.
 *    A misspelled or re-keyed param is not a 400 — it is silently dropped and
 *    the server's own default applies, so the sweep runs with settings the
 *    panel is not showing and nothing says so. Params are therefore passed
 *    through VERBATIM here: no renaming, no re-keying, no defaulting. The wire
 *    key names live in the params module, and this module must not paraphrase
 *    them.
 * 2. `POST /stations/batch` returns 201 EVEN WHEN ROWS FAILED (the controller
 *    says so in a capitalised comment). Its body is returned as-is so callers
 *    can read `failedCount`/`results`; collapsing it to a boolean would report
 *    "all created" over a partial failure.
 * 3. `ValidationPipe` returns `message` as an ARRAY of field errors, so error
 *    text belongs to `apiErrorMessage` from `helpers/api` — `error.message`
 *    alone is "Request failed with status code 400", which tells an operator
 *    nothing. Callers surface it; this module does not swallow rejections.
 *
 * `GeneratedCandidate` (flat `latitude`/`longitude` + `cityMongoId`) and
 * `StationBatchItemDto` (exactly those names, deliberately matching per its
 * doc comment) need NO coordinate mapping between them. Do not route
 * candidates through `StationsApi`'s `toRestPayload`/`toCompatShape`: those
 * exist for GraphQL-era consumers, their inbound/outbound asymmetry is
 * intentional (GeoJSON `coord` in, scalars out), and a candidate passed
 * through `toRestPayload` would come out coordinate-swapped.
 */

/**
 * `CandidateSearchDto` declares the four bbox scalars with `@Min`/`@Max` and
 * NO `@IsOptional` — they are required and un-defaulted, so a missing one is a
 * server-side 400 rather than a sensible fallback. Failing here names the
 * offending field instead. Same idiom as `LaneMapApi`'s `boundsParams`.
 */
const boundsFields = (bbox) => {
  const fields = {
    swLng: bbox?.swLng,
    swLat: bbox?.swLat,
    neLng: bbox?.neLng,
    neLat: bbox?.neLat,
  };
  const invalidKeys = Object.entries(fields)
    .filter(([, value]) => !Number.isFinite(value))
    .map(([key]) => key);
  if (invalidKeys.length > 0) {
    throw new Error(`Invalid bbox: ${invalidKeys.join(', ')} must be finite number(s)`);
  }
  return fields;
};

/**
 * `CandidateSearchDto` is FLAT: the bbox scalars sit beside the params, there
 * is no nested `bbox` key. Sending one would be whitelisted away entirely and
 * the request would 400 on four missing required fields.
 *
 * There is also deliberately no `cityId` on the DTO — the corridor filter
 * measures against lane geometry near the swept rectangle and each candidate's
 * city is derived from its own coordinates, so the selected city is frontend
 * state only. Do not add one; it would be stripped.
 */
const searchBody = (bbox, params) => ({ ...boundsFields(bbox), ...params });

/** What a sweep would cost. Spends nothing. Reports `overCap` rather than throwing. */
const estimateCandidates = async ({ bbox, params = {} } = {}) => {
  const { data } = await post('/admin/stations/candidates/estimate', searchBody(bbox, params));
  return data;
};

/**
 * Runs the sweep. Billable, and throws 400 over the cell/request cap — so
 * callers estimate first and block on `overCap`.
 *
 * Returns `{ candidates, stats }`. `stats.dropped` is a `Record<string, number>`
 * whose keys appear ONLY when that reason fired, so it is returned untouched;
 * do not normalise it into fixed fields.
 */
const generateCandidates = async ({ bbox, params = {} } = {}) => {
  const { data } = await post('/admin/stations/candidates', searchBody(bbox, params));
  return data;
};

/** Records vetoed candidates so later sweeps never re-propose them. Max 200 per call. */
const recordRejections = async ({ rejections }) => {
  const { data } = await post('/admin/stations/candidates/rejections', { rejections });
  return data;
};

/**
 * Creates many stations, rows independent. Max 100 per call.
 *
 * Returns `{ results, createdCount, failedCount }` UNCHANGED. A 2xx here is
 * not full success — see note 2 at the top of this file.
 */
const batchCreateStations = async ({ stations }) => {
  const { data } = await post('/admin/stations/batch', { stations });
  return data;
};

export default {
  estimateCandidates,
  generateCandidates,
  recordRejections,
  batchCreateStations,
};
